import { LitElement, html } from "lit";
import { customElement, state, query, property } from "lit/decorators.js";
import { provide } from "@lit/context";
import { APP_CONFIG } from "../config/attributes";
import { DATASET_COLORS } from "../constants";
import { themeStyles } from "../styles/themes";
import { appStyles } from "../styles/app.styles";
import { themeContext, configContext, lockedContext } from "../contexts";
import { PersistenceController } from "../controllers/persistence-controller";
import { ToastController } from "../controllers/toast-controller";
import { decodeRatings, resolveHighestVersion, upgradeStateToVersion } from "../utils/serialization";
import { validateColorDistinctness, findDistinctColor } from "../utils/color";
import type { Theme, RatingSet, RatingExport } from "../models";

import "./bab-attribute-rater";
import "./bab-spider-chart";
import "./bab-toast-manager";
import "./bab-import-modal";
import "./bab-app-header";
import "./bab-subject-tabs";
import "./bab-readonly-banner";
import "./bab-name-editor";
import "./bab-share-row";

/**
 * Top-level application shell.
 */
@customElement("bab-chart-app")
export class BabChartApp extends LitElement {
  static styles = [themeStyles, appStyles];

  @query("bab-spider-chart")
  private _chartComp!: import("./bab-spider-chart").BabSpiderChart;

  @property({ type: Object })
  @provide({ context: configContext })
  config = APP_CONFIG;

  @state()
  @provide({ context: themeContext })
  theme: Theme = "system";

  @state()
  @provide({ context: lockedContext })
  locked = false;

  @state() private _activeSetIndex = 0;
  @state() private _clearPending = false;
  @state() private _showImportModal = false;
  @state() private _chartExpanded = false;
  
  private _persistence: PersistenceController;
  private _toasts = new ToastController(this);
  private _clearTimer: ReturnType<typeof setTimeout> | null = null;
  private _colorChangeTimer: ReturnType<typeof setTimeout> | null = null;
  private _initialized = false;

  private get _activeVersion(): string {
    const v = this._persistence?.state.version;
    return (v && this.config.versions[v]) ? v : this.config.currentVersion;
  }

  private get _versionAttributes() {
    const ids = this.config.versions[this._activeVersion].attributeIds;
    const attrMap = new Map(this.config.attributes.map((a) => [a.id, a]));
    return ids.map((id) => attrMap.get(id)).filter((a) => a !== undefined);
  }

  constructor() {
    super();
    this._persistence = new PersistenceController(this, () => this.config);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._clearTimer !== null) {
      clearTimeout(this._clearTimer);
      this._clearTimer = null;
    }
    if (this._colorChangeTimer !== null) {
      clearTimeout(this._colorChangeTimer);
      this._colorChangeTimer = null;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    // State is loaded by hostConnected() inside super.connectedCallback().
    // Sync theme and lock only on the first connection.
    if (!this._initialized) {
      this._initialized = true;
      this.theme = this._persistence.state.theme || "system";
      const hasData = this._persistence.state.datasets.some(
        d => Object.values(d.ratings).some(r => r !== null)
      );
      this.locked = hasData;
    }
    this._updateThemeAttribute();
    document.title = this.config.title ?? "BAB Chart";
  }

  willUpdate(changedProperties: Map<string | number | symbol, unknown>) {
    super.willUpdate(changedProperties);

    if (this._persistence) {
      // Sync theme if persistence state changed (e.g. via hashchange).
      if (this._persistence.state.theme && this._persistence.state.theme !== this.theme) {
        this.theme = this._persistence.state.theme;
        this._updateThemeAttribute();
      }
      
      // Re-lock when the browser navigates to a new hash with data.
      if (this._persistence.externalHashChange) {
        this._persistence.externalHashChange = false;
        const hasData = this._persistence.state.datasets.some(
          d => Object.values(d.ratings).some(r => r !== null)
        );
        this.locked = hasData;
      }

      // Safety check for active index bounds
      if (this._activeSetIndex >= this._persistence.state.datasets.length) {
        this._activeSetIndex = Math.max(0, this._persistence.state.datasets.length - 1);
      }
    }
  }

  private _updateThemeAttribute() {
    this.setAttribute("data-theme", this.theme);
  }

  private _setTheme(theme: Theme) {
    this.theme = theme;
    this._persistence.update({ ...this._persistence.state, theme });
    this._updateThemeAttribute();
  }

  private _onRatingChanged(
    e: CustomEvent<{ attributeId: string; rating: number }>,
  ) {
    const datasets = [...this._persistence.state.datasets];
    datasets[this._activeSetIndex] = {
      ...datasets[this._activeSetIndex],
      ratings: {
        ...datasets[this._activeSetIndex].ratings,
        [e.detail.attributeId]: e.detail.rating,
      },
    };
    this._persistence.update({ ...this._persistence.state, datasets });
  }

  private _onNameChanged(e: CustomEvent<{ name: string }>) {
    const datasets = [...this._persistence.state.datasets];
    datasets[this._activeSetIndex] = {
      ...datasets[this._activeSetIndex],
      name: e.detail.name,
    };
    this._persistence.update({ ...this._persistence.state, datasets });
  }

  private _chartBackground(): string {
    return getComputedStyle(this).getPropertyValue("--bab-card-bg").trim() || "#ffffff";
  }

  private _onColorChanged(e: CustomEvent<{ index: number; color: string }>) {
    const datasets = [...this._persistence.state.datasets];
    datasets[e.detail.index] = { ...datasets[e.detail.index], color: e.detail.color };

    if (this._colorChangeTimer !== null) clearTimeout(this._colorChangeTimer);
    this._colorChangeTimer = setTimeout(() => {
      this._colorChangeTimer = null;

      const allColors = datasets.map(set => set.color || "#000000");
      const colorValidation = validateColorDistinctness(allColors, 4.0);
      if (!colorValidation.valid && colorValidation.conflicts) {
        const conflicts = colorValidation.conflicts
          .map(c => {
            const idx = c.indices[1];
            return datasets[idx]?.name || `Dataset ${idx + 1}`;
          })
          .join(", ");
        this._toasts.show(
          `⚠ This color is too similar to: ${conflicts}. Please choose a different color.`,
          "warn"
        );
      } else if (!validateColorDistinctness([e.detail.color, this._chartBackground()], 4.0).valid) {
        this._toasts.show(
          `⚠ This color is too similar to the chart background. Please choose a different color.`,
          "warn"
        );
      }

      this._persistence.update({ ...this._persistence.state, datasets });
    }, 80);
  }

  private _onLockChanged(e: CustomEvent<{ locked: boolean }>) {
    const nowLocked = e.detail.locked;
    this.locked = nowLocked;
    if (!nowLocked && this._activeVersion !== this.config.currentVersion) {
      // Upgrade all datasets to the current version when the user unlocks for editing.
      const upgraded = upgradeStateToVersion(
        this._persistence.state,
        this.config.currentVersion,
        this.config,
      );
      this._persistence.update(upgraded);
      const added = this.config.versions[this.config.currentVersion].attributeIds.filter(
        id => !this.config.versions[this._activeVersion]?.attributeIds.includes(id)
      );
      if (added.length) {
        this._toasts.show(
          `Upgraded to ${this.config.currentVersion}: ${added.length} new ${
            added.length === 1
              ? (this.config.attributeTerm ?? "attribute")
              : (this.config.attributeTermPlural ?? "attributes")
          } added`,
          "info",
        );
      }
    }
  }

  private _copyLink() {
    navigator.clipboard.writeText(window.location.href)
      .then(() => {
        this._toasts.show("Link copied to clipboard!");
      })
      .catch(() => {
        this._toasts.show("Could not copy link", "warn");
      });
  }

  private _clear() {
    if (!this._clearPending) {
      this._clearPending = true;
      this._clearTimer = setTimeout(() => {
        this._clearPending = false;
      }, 3000);
      return;
    }
    clearTimeout(this._clearTimer!);
    this._clearPending = false;
    
    const defaultRatings: Record<string, number | null> = {};
    for (const attr of this._versionAttributes) {
      defaultRatings[attr.id] = null;
    }

    this._persistence.update({
      ...this._persistence.state,
      datasets: [{ ratings: defaultRatings, color: DATASET_COLORS[0] }],
      version: this.config.currentVersion,
    });
    this._activeSetIndex = 0;
    this.locked = false;
    this._toasts.show("Chart cleared", "info");
  }

  private _addDataset() {
    this.locked = false;
    const defaultRatings: Record<string, number | null> = {};
    for (const attr of this._versionAttributes) {
      defaultRatings[attr.id] = null;
    }
    
    // Find a color distinct from all existing datasets and the chart background
    const existingColors = this._persistence.state.datasets.map(
      (set, i) => set.color || DATASET_COLORS[i % DATASET_COLORS.length]
    );
    const color = findDistinctColor([this._chartBackground(), ...existingColors]) || DATASET_COLORS[this._persistence.state.datasets.length % DATASET_COLORS.length];

    const newSet: RatingSet = {
      ratings: defaultRatings,
      name: `${this._persistence.state.datasets.length + 1}`,
      color
    };
    const newDatasets = [...this._persistence.state.datasets, newSet];
    this._persistence.update({
      ...this._persistence.state,
      datasets: newDatasets,
    });
    this._activeSetIndex = newDatasets.length - 1;
    this._toasts.show("Added new comparison");
  }

  private _duplicateDataset(index: number) {
    if (this.locked) return;
    
    const source = this._persistence.state.datasets[index];
    
    // Find a color distinct from all existing datasets and the chart background
    const existingColors = this._persistence.state.datasets.map(
      (set, i) => set.color || DATASET_COLORS[i % DATASET_COLORS.length]
    );
    const color = findDistinctColor([this._chartBackground(), ...existingColors]) || DATASET_COLORS[this._persistence.state.datasets.length % DATASET_COLORS.length];

    const newSet: RatingSet = {
      ratings: { ...source.ratings },
      name: `${source.name || (index + 1)} (Copy)`,
      color
    };
    
    const datasets = [...this._persistence.state.datasets];
    datasets.splice(index + 1, 0, newSet);
    
    this._persistence.update({ ...this._persistence.state, datasets });
    this._activeSetIndex = index + 1;
    this._toasts.show("Dataset duplicated");
  }

  private _removeDataset(index: number) {
    if (this.locked || this._persistence.state.datasets.length <= 1) return;
    
    const oldActiveIndex = this._activeSetIndex;
    const datasets = this._persistence.state.datasets.filter((_, i) => i !== index);
    this._persistence.update({ ...this._persistence.state, datasets });
    
    if (index < oldActiveIndex) {
      this._activeSetIndex = oldActiveIndex - 1;
    } else if (oldActiveIndex >= datasets.length) {
      this._activeSetIndex = datasets.length - 1;
    }
    
    this._toasts.show("Dataset removed", "info");
  }

  private _importDataset(url: string) {
    try {
      const hash = url.includes("#") ? url.slice(url.indexOf("#")) : `#${url}`;
      const state = decodeRatings(hash, this.config);
      
      if (state.datasets.length > 0) {
        this.locked = false;

        // Start with the existing datasets as the base; assign default colors where missing.
        const existingSets = this._persistence.state.datasets.map((set, i) => ({
          ...set,
          color: set.color || DATASET_COLORS[i % DATASET_COLORS.length],
        }));

        // Re-color each incoming dataset if its color conflicts with any already-committed color or the background.
        const bg = this._chartBackground();
        let recoloredCount = 0;
        const importedSets = state.datasets.map((set) => {
          const committed = existingSets.map(s => s.color!);
          const incomingColor = set.color || DATASET_COLORS[existingSets.length % DATASET_COLORS.length];
          const { valid } = validateColorDistinctness([incomingColor, bg, ...committed]);

          const finalColor = (() => {
            if (!valid) {
              recoloredCount++;
              return findDistinctColor([bg, ...committed]) ?? incomingColor;
            }
            return incomingColor;
          })();

          // Record the committed color so subsequent imports avoid it too.
          existingSets.push({ ...set, color: finalColor });
          return { ...set, color: finalColor };
        });

        const newSets = [...this._persistence.state.datasets.map((set, i) => ({
          ...set,
          color: set.color || DATASET_COLORS[i % DATASET_COLORS.length],
        })), ...importedSets];

        // Resolve the highest version between the existing state and the imported state
        const existingVersion = this._persistence.state.version ?? this.config.currentVersion;
        const importedVersion = state.version ?? this.config.currentVersion;
        const mergedVersion = resolveHighestVersion([existingVersion, importedVersion], this.config);

        const mergedState = upgradeStateToVersion(
          { ...this._persistence.state, datasets: newSets, version: mergedVersion },
          mergedVersion,
          this.config,
        );
        this._persistence.update(mergedState);
        this._activeSetIndex = this._persistence.state.datasets.length - 1;

        const label = state.datasets.length === 1 ? "1 dataset" : `${state.datasets.length} datasets`;
        const recolorNote = recoloredCount > 0
          ? ` (${recoloredCount} color${recoloredCount > 1 ? "s" : ""} adjusted for visibility)`
          : "";
        this._toasts.show(`Imported ${label}${recolorNote}`);
        this._showImportModal = false;
      } else {
        this._toasts.show("Invalid URL or hash", "warn");
      }
    } catch {
      this._toasts.show("Failed to import", "warn");
    }
  }

  getRatings(): RatingExport[] {
    const version = this._persistence.state.version ?? this.config.currentVersion;
    const { attributeIds } = this.config.versions[version] ?? this.config.versions[this.config.currentVersion];
    return this._persistence.state.datasets.map(dataset => ({
      name: dataset.name,
      version,
      color: dataset.color,
      ratings: Object.fromEntries(
        attributeIds.map(id => [id, dataset.ratings[id] ?? null])
      ),
    }));
  }

  private _downloadImage(format: "png" | "svg" = "png") {
    const dataUrl = format === "png"
      ? this._chartComp.getImageDataUrl()
      : this._chartComp.getSvgDataUrl();
    const link = document.createElement("a");
    const name = this._persistence.state.datasets[this._activeSetIndex]?.name?.trim() || `${this.config.attributeTerm || "skill"}-chart`;
    link.download = `${name.replace(/\s+/g, "-").toLowerCase()}.${format}`;
    link.href = dataUrl;
    link.click();
    this._toasts.show(`${format.toUpperCase()} saved!`);
  }

  render() {
    const activeSet = this._persistence.state.datasets[this._activeSetIndex];
    const versionAttributes = this._versionAttributes;

    return html`
      <div class="content">
        <bab-app-header 
          .theme="${this.theme}"
          @theme-changed="${(e: CustomEvent<{theme: Theme}>) => this._setTheme(e.detail.theme)}"
        ></bab-app-header>

        <div class="banner-slot">
          <bab-readonly-banner .active="${this.locked}"></bab-readonly-banner>
        </div>

        <bab-name-editor
          .name="${activeSet?.name ?? ""}"
          .locked="${this.locked}"
          @name-changed="${this._onNameChanged}"
          @lock-changed="${this._onLockChanged}"
        ></bab-name-editor>

        <bab-subject-tabs
          .datasets="${this._persistence.state.datasets}"
          .activeIndex="${this._activeSetIndex}"
          .locked="${this.locked}"
          @tab-changed="${(e: CustomEvent<{index: number}>) => this._activeSetIndex = e.detail.index}"
          @add-dataset="${this._addDataset}"
          @duplicate-dataset="${(e: CustomEvent<{index: number}>) => this._duplicateDataset(e.detail.index)}"
          @remove-dataset="${(e: CustomEvent<{index: number}>) => this._removeDataset(e.detail.index)}"
          @color-changed="${this._onColorChanged}"
          @open-import="${() => this._showImportModal = true}"
        ></bab-subject-tabs>

        <div class="layout">
          <div id="raters-panel" class="raters-panel ${this.locked ? "raters-panel--locked" : ""}" role="tabpanel">
            <div class="raters-header">
              <span class="raters-title">
                ${(this.config.attributeTermPlural || "Skills").charAt(0).toUpperCase() + (this.config.attributeTermPlural || "Skills").slice(1)}
                ${activeSet?.name ? `: ${activeSet.name}` : ""}
              </span>
              <span class="completion-badge ${
                versionAttributes.filter(a => (activeSet?.ratings[a.id] ?? null) !== null).length === versionAttributes.length
                  ? "completion-badge--complete" : ""
              }">
                ${versionAttributes.filter(a => (activeSet?.ratings[a.id] ?? null) !== null).length} / ${versionAttributes.length}
              </span>
            </div>

            ${versionAttributes.map(
              (attr) => html`
                <bab-attribute-rater
                  .attribute="${attr}"
                  .rating="${activeSet?.ratings[attr.id] ?? null}"
                  .versionConfig="${this.config.versions[this._activeVersion]}"
                  ?disabled="${this.locked}"
                  @rating-changed="${this._onRatingChanged}"
                ></bab-attribute-rater>
              `,
            )}
          </div>

          <div class="chart-panel ${this._chartExpanded ? "chart-panel--expanded" : ""}">
            <bab-spider-chart
              .datasets="${this._persistence.state.datasets}"
              .activeVersion="${this._activeVersion}"
              .theme="${this.theme}"
              data-theme="${this.theme}"
              @toggle-expand="${(e: CustomEvent<{expanded: boolean}>) => this._chartExpanded = e.detail.expanded}"
            ></bab-spider-chart>
          </div>
        </div>

        <bab-share-row
          .locked="${this.locked}"
          .clearPending="${this._clearPending}"
          @download-png="${() => this._downloadImage("png")}"
          @download-svg="${() => this._downloadImage("svg")}"
          @copy-link="${this._copyLink}"
          @clear-requested="${this._clear}"
        ></bab-share-row>
      </div>

      <bab-import-modal
        .open="${this._showImportModal}"
        @close="${() => this._showImportModal = false}"
        @import="${(e: CustomEvent<{url: string}>) => this._importDataset(e.detail.url)}"
      ></bab-import-modal>

      <bab-toast-manager .toasts="${this._toasts.toasts}"></bab-toast-manager>
    `;
  }
}
